import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/projects/components/ui/sheet";
import type { Submission } from "@/projects/lib/store";
import { StatusBadge } from "@/projects/components/status-badge";
import { FileVideo, Play } from "lucide-react";
import { Button } from "@/projects/components/ui/button";
import { useState } from "react";
import { VideoPlayerModal } from "@/projects/components/video-player-modal";

export function SubmissionDetailsDrawer({
  submission,
  open,
  onClose,
}: {
  submission: Submission | null;
  open: boolean;
  onClose: () => void;
}) {
  const [videoOpen, setVideoOpen] = useState(false);
  if (!submission) {
    return null;
  }

  return (
    <>
      <Sheet open={open} onOpenChange={o => !o && onClose()}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <span className="text-royal font-mono">{submission.id}</span>
              <StatusBadge status={submission.status} />
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            <Section title="Student Information">
              <Row k="Name" v={submission.studentName} />
              <Row k="Student ID" v={submission.studentId} />
              {submission.pathshalaName && (
                <Row k="Pathshala" v={submission.pathshalaName} />
              )}
              {submission.phoneNumber && (
                <Row k="Phone" v={submission.phoneNumber} />
              )}
              {submission.birthDate && (
                <Row k="Date of Birth" v={formatDob(submission.birthDate)} />
              )}
              {submission.age !== undefined && (
                <Row k="Age" v={`${submission.age} years`} />
              )}
            </Section>

            <Section title="Video Information">
              <Row k="Title" v={submission.videoTitle} />
              {submission.topicName && (
                <Row k="Topic" v={submission.topicName} />
              )}
              <Row k="File" v={submission.fileName} />
              <Row k="Size" v={`${submission.fileSizeMB} MB`} />
              {submission.remark && <Row k="Remark" v={submission.remark} />}
              <Button
                variant="outline"
                size="sm"
                className="mt-2 w-full"
                onClick={() => setVideoOpen(true)}
              >
                <Play className="mr-2 h-4 w-4" /> Play Video
              </Button>
            </Section>

            <Section title="Submission">
              <Row
                k="Reference"
                v={<span className="font-mono">{submission.id}</span>}
              />
              <Row
                k="Submitted"
                v={new Date(submission.submittedAt).toLocaleString()}
              />
              <Row k="Status" v={<StatusBadge status={submission.status} />} />
            </Section>

            {submission.assignedTeacherName && (
              <Section title="Assignment">
                <Row k="Teacher" v={submission.assignedTeacherName} />
                <Row k="Assigned by" v="Super Admin" />
                <Row
                  k="Assigned at"
                  v={
                    submission.assignedAt
                      ? new Date(submission.assignedAt).toLocaleString()
                      : "—"
                  }
                />
                {submission.assignmentNotes && (
                  <Row k="Notes" v={submission.assignmentNotes} />
                )}
              </Section>
            )}

            {submission.verifiedAt && (
              <Section title="Verification">
                <Row
                  k="Verified by"
                  v={submission.assignedTeacherName ?? "—"}
                />
                <Row
                  k="Result"
                  v={<StatusBadge status={submission.status} />}
                />
                <Row
                  k="Verified at"
                  v={new Date(submission.verifiedAt).toLocaleString()}
                />
                {submission.teacherRemarks && (
                  <Row k="Remarks" v={submission.teacherRemarks} />
                )}
              </Section>
            )}

            <Section title="Activity Timeline">
              <ol className="relative space-y-4 border-l pl-6">
                {submission.timeline.map(e => (
                  <li key={`${e.at}-${e.label}`} className="relative">
                    <div className="border-background bg-royal absolute top-1 -left-[27px] grid h-4 w-4 place-items-center rounded-full border-2">
                      <FileVideo className="h-2 w-2 text-white" />
                    </div>
                    <div className="text-sm font-medium">{e.label}</div>
                    <div className="text-muted-foreground text-xs">
                      {new Date(e.at).toLocaleString()}
                    </div>
                  </li>
                ))}
              </ol>
            </Section>
          </div>
        </SheetContent>
      </Sheet>
      <VideoPlayerModal
        submission={submission}
        open={videoOpen}
        onOpenChange={setVideoOpen}
      />
    </>
  );
}

function formatDob(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-muted-foreground mb-2 text-xs font-semibold tracking-wider uppercase">
        {title}
      </div>
      <div className="bg-muted/30 space-y-1.5 rounded-lg border p-3 text-sm">
        {children}
      </div>
    </div>
  );
}
function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-muted-foreground shrink-0">{k}</div>
      <div className="text-right font-medium">{v}</div>
    </div>
  );
}
